import Vue from 'vue';
import FieldGroup from './FieldGroup';
import {
    checkRequired,
    checkMaxLength,
    checkMinLength
} from '@/common/validationRules';
import { isEmpty } from '@/common/helpers';

// This is a High Order Component which will take fromLayout component as a parameter
// And will generate new component that inject the fields into the formLayout
const HocFormGenerator = formLayout => {
    return Vue.component('hoc-form-generator', {
        components: {
            'field-group': FieldGroup
        },
        props: {
            // array of field definitions
            schema: {
                type: Array,
                required: true
            },
            // object of key: value pair for each field that needs to be rendered
            value: {
                type: Object,
                required: false,
                default: () => {}
            }
        },
        data() {
            return {
                formData: {},
                componentKey: 0
            };
        },
        computed: {
            reactiveValue: {
                get() {
                    return this.value;
                },
                set(newValue) {
                    this.$emit('input', newValue);
                }
            },
            augmentedSchema() {
                // need to insert validation rules into field schemas....
                // will only inject rules for the followings
                // required, maxlength, minlength
                let newSchema = this.schema.map(field => {
                    const { component, ...restField } = field || {};
                    const { props, ...restComponent } = component || {};
                    const {
                        rules,
                        required,
                        maxlength,
                        minlength,
                        ...restProps
                    } = props || {};
                    let fieldLabel = field.label || props.label || 'Field';
                    fieldLabel = fieldLabel.replace(/\s\*$/, '');
                    let injectRules = [];
                    // -------------------- rules ---------------------
                    if (required) {
                        injectRules.push(v => checkRequired(v, fieldLabel));
                    }
                    if (typeof maxlength === 'number') {
                        injectRules.push(v =>
                            checkMaxLength(v, maxlength, fieldLabel)
                        );
                    }
                    if (typeof minlength === 'number') {
                        injectRules.push(v =>
                            checkMinLength(v, minlength, fieldLabel)
                        );
                    }

                    const newRules = [...(rules || []), ...injectRules];
                    let newProps = { ...restProps, maxlength };
                    if (newRules.length) {
                        newProps.rules = newRules;
                    }
                    let newComponent = { ...restComponent };
                    if (!isEmpty(newProps)) {
                        newComponent.props = newProps;
                    }
                    let newField = { ...restField };
                    if (!isEmpty(newComponent)) {
                        newField.component = newComponent;
                    }
                    return newField;
                });
                return newSchema;
            }
        },
        watch: {
            schema: {
                deep: true,
                handler() {
                    // need to re-render the componet if the schema changes...
                    // useful if we have component props tied to vuex or async funtion
                    this.componentKey++;
                }
            }
        },
        methods: {
            onFieldChange(value, field) {
                let formValue = Object.assign({}, this.reactiveValue);
                formValue[field] = value;
                this.reactiveValue = formValue;
            }
        },
        render(h) {
            // will inject the fields into the formLayout slots...
            const fieldSlots = this.augmentedSchema.map(field => {
                const fieldGroup = h('field-group', {
                    key: `field-group-${field.name}`,
                    props: {
                        ...field,
                        ...this.$attrs,
                        value: this.reactiveValue[field.name]
                    },
                    on: {
                        input: event => this.onFieldChange(event, field.name)
                    }
                });
                return h('template', { slot: field.name }, [fieldGroup]);
            });
            return h(
                formLayout,
                {
                    key: `form-gen-${this.componentKey}`,
                    props: {
                        schema: this.schema
                    }
                },
                fieldSlots
            );
        }
    });
};

export default HocFormGenerator;
